<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import BrandLogo from "../components/BrandLogo.vue";
import ResultView from "../components/ResultView.vue";
const route = useRoute(),
  data = ref<any>(),
  error = ref(""),
  busy = ref(false),
  connection = ref("Forbinder…");
const base = `/api/public/dashboards/${encodeURIComponent(String(route.params.org))}/${encodeURIComponent(String(route.params.token))}`;
let stream: EventSource | undefined,
  timer: ReturnType<typeof setInterval> | undefined,
  refresh: ReturnType<typeof setTimeout> | undefined,
  request = 0,
  revoked = false;
function revoke() {
  revoked = true;
  request++;
  stream?.close();
  clearInterval(timer);
  clearTimeout(refresh);
  data.value = undefined;
  busy.value = false;
  error.value = "Linket er ikke tilgængeligt eller er slået fra.";
  connection.value = "Adgang lukket";
}
async function load() {
  if (revoked) return;
  const id = ++request;
  busy.value = true;
  try {
    const response = await fetch(base, { credentials: "omit" });
    const next = await response.json();
    if (id !== request) return;
    if (response.status === 404) {
      revoke();
      return;
    }
    if (!response.ok) throw Error(next.message);
    data.value = next;
    error.value = "";
    document.title = next.name + " · Yawhub";
  } catch (e) {
    if (id === request)
      error.value = e instanceof Error ? e.message : "Data kunne ikke hentes.";
  } finally {
    if (id === request) busy.value = false;
  }
}
onMounted(() => {
  void load();
  stream = new EventSource(base + "/events");
  stream.onopen = () => {
    connection.value = "Opdateres live";
  };
  stream.addEventListener("change", () => {
    clearTimeout(refresh);
    refresh = setTimeout(() => void load(), 150);
  });
  stream.addEventListener("revoked", revoke);
  stream.addEventListener("unavailable", () => {
    connection.value = "Forbindelsen gendannes…";
  });
  stream.onerror = () => {
    if (!revoked) connection.value = "Forbindelsen gendannes…";
  };
  timer = setInterval(() => {
    if (!revoked && stream?.readyState !== EventSource.OPEN) void load();
  }, 30000);
});
onUnmounted(() => {
  request++;
  stream?.close();
  clearInterval(timer);
  clearTimeout(refresh);
  document.title = "Yawhub";
});
</script>
<template>
  <main class="public-dashboard">
    <header>
      <a href="/" class="brand" aria-label="Yawhub"><BrandLogo /></a
      ><span class="muted small">Delt dashboard · læseadgang</span>
    </header>
    <p v-if="!data && busy" role="status">Henter dashboard…</p>
    <div v-if="error" class="notice" role="alert">
      <p>{{ error }}</p>
      <button
        v-if="!revoked"
        class="button secondary"
        :disabled="busy"
        @click="load"
      >
        Prøv igen
      </button>
    </div>
    <template v-if="data">
      <div class="section-heading">
        <div>
          <h1>{{ data.name }}</h1>
          <p class="muted" role="status">{{ connection }}</p>
        </div>
        <button class="button secondary" :disabled="busy" @click="load">
          {{ busy ? "Opdaterer…" : "Opdatér" }}
        </button>
      </div>
      <div class="public-dashboard-grid">
        <article
          v-for="card in data.cards"
          :key="card.id"
          class="public-dashboard-card"
          :class="{ wide: ['calendar', 'table'].includes(card.kind) }"
        >
          <h2>{{ card.name }}</h2>
          <ResultView
            :result="card.result"
            :kind="card.kind"
            :field-ids="card.config.fieldIds"
            :scale-bars="card.config.scaleBars"
            :show-updated-at="card.config.showUpdatedAt"
            :view-config="card.config"
            :page-url="base + '/cards/' + card.id"
            public-page
            compact
          />
        </article>
      </div>
    </template>
  </main>
</template>
<style scoped lang="scss" src="../styles/pages/PublicDashboard.scss"></style>
